import { useCallback, useEffect, useRef, useState } from 'react';

type SpeechRecognitionEventCallback = (event: SpeechRecognitionEvent) => void;

interface UseSpeechRecognitionOptions {
  language?: string;
  onResult?: (transcript: string, event: SpeechRecognitionEvent) => void;
  onError?: (event: Event) => void;
  onStart?: () => void;
  onEnd?: () => void;
}

const getSpeechRecognitionConstructor = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const useSpeechRecognition = ({
  language = 'en-US',
  onResult,
  onError,
  onStart,
  onEnd,
}: UseSpeechRecognitionOptions = {}) => {
  const recognitionRef = useRef<any>(null);
  const [isSupported, setIsSupported] = useState(false);
  const [isListening, setIsListening] = useState(false);

  useEffect(() => {
    const Constructor = getSpeechRecognitionConstructor();
    setIsSupported(!!Constructor);

    return () => {
      recognitionRef.current?.stop();
      recognitionRef.current = null;
    };
  }, []);

  const startListening = useCallback(() => {
    if (isListening) return;
    const Constructor = getSpeechRecognitionConstructor();
    if (!Constructor) return;

    const recognition = new Constructor();
    recognition.lang = language;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => {
      setIsListening(true);
      onStart?.();
    };

    recognition.onresult = ((event: SpeechRecognitionEvent) => {
      const transcript = event.results?.[0]?.[0]?.transcript;
      if (transcript) {
        onResult?.(transcript, event);
      }
    }) as SpeechRecognitionEventCallback;

    recognition.onerror = (event: Event) => {
      onError?.(event);
      setIsListening(false);
    };

    recognition.onend = () => {
      setIsListening(false);
      onEnd?.();
      recognitionRef.current = null;
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, [isListening, language, onEnd, onError, onResult, onStart]);

  const stopListening = useCallback(() => {
    recognitionRef.current?.stop();
  }, []);

  return {
    startListening,
    stopListening,
    isSupported,
    isListening,
  };
};

export default useSpeechRecognition;
